import type { FlightItinerary } from '@shared/types';
import { formatTimeLabel } from '@/lib/formatting';
import { routeCodes } from '@/lib/flightRoute';

function durationLabel(minutes: number | null): string {
  if (minutes == null) return '';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${m}m`;
}

export function FlightCard({
  itinerary,
  onSave,
  saved,
}: {
  itinerary: FlightItinerary;
  onSave?: () => void;
  saved?: boolean;
}) {
  const seg = itinerary.segments[0];
  const isUnitedOperated = seg.operator_iata === 'UA' || seg.operator?.toUpperCase().includes('UNITED');

  return (
    <div className="card">
      <div className="place-card-header">
        <div>
          <span className={`badge ${itinerary.nonstop ? 'badge-open' : 'badge-neutral'}`}>
            {itinerary.nonstop ? 'NONSTOP' : 'CONNECTION'}
          </span>
          <p style={{ marginTop: 6, fontSize: 13, fontWeight: 700, letterSpacing: '0.02em', color: 'var(--color-ink)' }}>
            {routeCodes(itinerary)}
          </p>
          <h3 style={{ marginTop: 4, fontSize: 20, fontWeight: 800 }}>
            {formatTimeLabel(seg.scheduled_out)} → {formatTimeLabel(seg.scheduled_in)}
          </h3>
          <p style={{ color: 'var(--color-ink-muted)', fontSize: 13, marginTop: 2 }}>
            {seg.ident} · {durationLabel(itinerary.totalDurationMinutes)}
          </p>
        </div>
      </div>

      <p style={{ fontSize: 13, marginTop: 8 }}>
        Marketed as {seg.marketing_carrier ?? 'United Airlines'}
        {!isUnitedOperated && seg.operator ? ` · Operated by ${seg.operator}` : ''}
        {seg.aircraft_type ? ` · ${seg.aircraft_type}` : ''}
      </p>

      {itinerary.fare ? (
        <p style={{ marginTop: 10, fontWeight: 700 }}>
          ${itinerary.fare.amountPerTraveler} per traveler
          <span style={{ fontWeight: 400, color: 'var(--color-ink-muted)', fontSize: 13 }}>
            {' '}
            · Checked {new Date(itinerary.fare.checkedAt).toLocaleTimeString()}
          </span>
        </p>
      ) : (
        <a
          href="https://www.united.com"
          target="_blank"
          rel="noreferrer"
          className="btn btn-ghost btn-sm"
          style={{ marginTop: 10 }}
        >
          Check current fare on United
        </a>
      )}

      {onSave && (
        <button type="button" className="btn btn-primary btn-block" style={{ marginTop: 12 }} onClick={onSave} disabled={saved}>
          {saved ? 'Saved' : 'Save This Flight'}
        </button>
      )}
    </div>
  );
}
